import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import * as A from './playlistActions';
import type { Deferred } from './playlistTypes';
import type { DisplayOrientation, Playlist } from '../../../components/display/data/types';

export function usePlaylistStore() {
  const dispatch = useAppDispatch();
  const { items, loading, error } = useAppSelector((s) => s.playlist);

  const fetchPlaylists = useCallback((locationId: string) => dispatch(A.playlistFetchRequest({ locationId })), [dispatch]);

  const createPlaylist = useCallback(
    (locationId: string, name: string, orientation: DisplayOrientation, mediaIds: string[]): Promise<Playlist> => {
      return new Promise<Playlist>((resolve, reject) => {
        const deferred: Deferred<Playlist> = { resolve, reject };
        dispatch(A.playlistCreateRequest({ locationId, name, orientation, mediaIds, deferred }));
      });
    },
    [dispatch],
  );

  const updatePlaylist = useCallback(
    (id: string, locationId: string, name: string, orientation: DisplayOrientation, mediaIds: string[]): Promise<Playlist> => {
      return new Promise<Playlist>((resolve, reject) => {
        const deferred: Deferred<Playlist> = { resolve, reject };
        dispatch(A.playlistUpdateRequest({ id, locationId, name, orientation, mediaIds, deferred }));
      });
    },
    [dispatch],
  );

  const deletePlaylist = useCallback(
    (id: string, locationId: string): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        const deferred: Deferred = { resolve, reject };
        dispatch(A.playlistDeleteRequest({ id, locationId, deferred }));
      });
    },
    [dispatch],
  );

  return useMemo(
    () => ({ items, loading, error, fetchPlaylists, createPlaylist, updatePlaylist, deletePlaylist }),
    [items, loading, error, fetchPlaylists, createPlaylist, updatePlaylist, deletePlaylist],
  );
}
